import React, { useCallback, useEffect, useState, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
interface ScannedProduct {
  title: string;
  editionLabel: string;
  priceNumber: number;
  priceText: string;
  thumbnailUrl: string;
}
const NAMES = ['María G.', 'Ana L.', 'Carolina P.', 'Sofía R.', 'Valentina M.', 'Lucía T.', 'Isabella F.', 'Camila D.', 'Daniela S.', 'Gabriela H.', 'Laura V.', 'Andrea C.', 'Paula B.', 'Natalia K.', 'Fernanda O.', 'Alejandra N.', 'Diana J.', 'Mariana E.', 'Claudia W.', 'Patricia A.', 'Elena R.', 'Carmen S.', 'Rosa M.', 'Teresa L.', 'Verónica P.', 'Adriana G.', 'Mónica T.', 'Silvia D.', 'Lorena F.', 'Beatriz H.'];
const CITIES = ['Miami, FL', 'Los Angeles, CA', 'New York, NY', 'Houston, TX', 'Chicago, IL', 'Phoenix, AZ', 'San Antonio, TX', 'San Diego, CA', 'Dallas, TX', 'Austin, TX', 'Denver, CO', 'Seattle, WA', 'Orlando, FL', 'Atlanta, GA', 'Las Vegas, NV', 'Portland, OR', 'San Francisco, CA', 'Tampa, FL', 'Charlotte, NC', 'Nashville, TN', 'CDMX, México', 'Madrid, España', 'Buenos Aires, Argentina', 'Bogotá, Colombia', 'Lima, Perú', 'Santiago, Chile'];
const FALLBACK_PRODUCTS: ScannedProduct[] = [{
  title: 'El Protocolo de la Piel de Lujo',
  editionLabel: 'Pack Completo',
  priceNumber: 11.99,
  priceText: '$11.99 USD',
  thumbnailUrl: 'https://cdn.magicpatterns.com/uploads/3SFkd3Ug74Tk38d8ci9GXG/mockup-1.png'
}, {
  title: 'La Biblia de las Mezclas',
  editionLabel: 'Seguridad Total',
  priceNumber: 10.99,
  priceText: '$10.99 USD',
  thumbnailUrl: 'https://cdn.magicpatterns.com/uploads/pFnqeaMsPQXszi28qrj3Ye/pl-ob-1.png'
}, {
  title: 'Skin-Tracker Pro',
  editionLabel: 'Disciplina de Lujo',
  priceNumber: 9.99,
  priceText: '$9.99 USD',
  thumbnailUrl: 'https://cdn.magicpatterns.com/uploads/bhcKtikC5eJdgc4EnYcDS5/pl-ob-2.png'
}];
function scanPageProducts(): ScannedProduct[] {
  const products: ScannedProduct[] = [];
  try {
    const scripts = document.querySelectorAll('script[type="application/ld+json"]');
    scripts.forEach(script => {
      try {
        const data = JSON.parse(script.textContent || '');
        if (data['@type'] === 'Product' && data.offers) {
          const offer = Array.isArray(data.offers) ? data.offers[0] : data.offers;
          products.push({
            title: data.name || 'Product',
            editionLabel: data.description || '',
            priceNumber: parseFloat(offer.price) || 0,
            priceText: `$${offer.price} ${offer.priceCurrency || 'USD'}`,
            thumbnailUrl: (Array.isArray(data.image) ? data.image[0] : data.image) || ''
          });
        }
      } catch {}
    });
    if (products.length > 0) return products;
    const priceRegex = /\$\s?(\d+(?:\.\d{2})?)/g;
    const priceElements = document.querySelectorAll('[class*="price"], [class*="total"], [data-price]');
    priceElements.forEach(el => {
      const match = priceRegex.exec(el.textContent || '');
      if (match) {
        const nearImg = el.closest('section')?.querySelector('img');
        products.push({
          title: 'El Protocolo de la Piel de Lujo',
          editionLabel: 'Edición Digital',
          priceNumber: parseFloat(match[1]),
          priceText: `$${match[1]} USD`,
          thumbnailUrl: nearImg?.src || ''
        });
      }
      priceRegex.lastIndex = 0;
    });
  } catch {}
  return products.length > 0 ? products : [];
}
function getRandomItem<T>(arr: T[]): T {
  return arr[Math.floor(Math.random() * arr.length)];
}
function getRandomInterval(): number {
  return Math.floor(Math.random() * 7000) + 8000;
}
interface Notification {
  id: number;
  name: string;
  city: string;
  product: ScannedProduct;
}
export function RecentPurchaseToast() {
  const [notification, setNotification] = useState<Notification | null>(null);
  const [visible, setVisible] = useState(false);
  const productsRef = useRef<ScannedProduct[]>([]);
  const nameHistoryRef = useRef<string[]>([]);
  const idRef = useRef(0);
  const timerRef = useRef<ReturnType<typeof setTimeout>>();
  const prefersReducedMotion = useRef(typeof window !== 'undefined' ? window.matchMedia('(prefers-reduced-motion: reduce)').matches : false);
  const pickName = useCallback((): string => {
    const history = nameHistoryRef.current;
    const available = NAMES.filter(n => !history.includes(n));
    const pool = available.length > 0 ? available : NAMES;
    const name = getRandomItem(pool);
    history.push(name);
    if (history.length > 10) history.shift();
    return name;
  }, []);
  const pickProduct = useCallback((): ScannedProduct => {
    const catalog = productsRef.current;
    if (catalog.length > 0) {
      const rand = Math.random();
      if (rand < 0.6) return catalog[0];
      return getRandomItem(catalog);
    }
    const rand = Math.random();
    return rand < 0.7 ? FALLBACK_PRODUCTS[0] : getRandomItem(FALLBACK_PRODUCTS.slice(1));
  }, []);
  const showNotification = useCallback(() => {
    const n: Notification = {
      id: ++idRef.current,
      name: pickName(),
      city: getRandomItem(CITIES),
      product: pickProduct()
    };
    setNotification(n);
    setVisible(true);
    setTimeout(() => {
      setVisible(false);
    }, 4000);
    timerRef.current = setTimeout(() => {
      showNotification();
    }, getRandomInterval() + 4500);
  }, [pickName, pickProduct]);
  useEffect(() => {
    const scanned = scanPageProducts();
    productsRef.current = scanned.length > 0 ? scanned : FALLBACK_PRODUCTS;
    const initialTimer = setTimeout(() => {
      showNotification();
    }, 3000);
    return () => {
      clearTimeout(initialTimer);
      if (timerRef.current) clearTimeout(timerRef.current);
    };
  }, [showNotification]);
  const motionProps = prefersReducedMotion.current ? {} : {
    initial: {
      y: 80,
      opacity: 0
    },
    animate: {
      y: 0,
      opacity: 1
    },
    exit: {
      y: 80,
      opacity: 0
    },
    transition: {
      type: 'spring',
      stiffness: 400,
      damping: 30
    }
  };
  return <div className="fixed bottom-6 left-1/2 -translate-x-1/2 z-[100] pointer-events-none" data-id="element-177">
      <AnimatePresence data-id="element-178">
        {visible && notification && <motion.div key={notification.id} {...motionProps} className="pointer-events-auto bg-white rounded-2xl shadow-2xl border border-charcoal/10 px-4 py-3 flex items-center gap-3 max-w-sm w-max" data-id="element-179">
            {notification.product.thumbnailUrl && <img src={notification.product.thumbnailUrl} alt={notification.product.title} className="w-12 h-12 rounded-lg object-cover shrink-0" data-id="element-180" />}
            <div className="text-left min-w-0" data-id="element-181">
              <p className="text-sm text-charcoal leading-snug" data-id="element-182">
                <span className="font-bold" data-id="element-183">{notification.name}</span>
                <span className="text-charcoal/50" data-id="element-184"> en </span>
                <span className="font-medium" data-id="element-185">{notification.city}</span>
              </p>
              <p className="text-xs text-charcoal/60 leading-snug mt-0.5" data-id="element-186">
                compró{' '}
                <span className="font-semibold text-charcoal/80" data-id="element-187">
                  {notification.product.editionLabel || notification.product.title}
                </span>
                {' — '}
                <span className="font-bold text-gold" data-id="element-188">
                  {notification.product.priceText}
                </span>
              </p>
            </div>
          </motion.div>}
      </AnimatePresence>
    </div>;
}